import { toPlainMessage } from '@bufbuild/protobuf';
import {
  createAsyncThunk,
  miniSerializeError,
  type SerializedError,
} from '@reduxjs/toolkit';

import {
  getTemplateIssues,
  isTemplateSeriesBusyError,
  isTemplateSeriesMovedError,
} from '@/shared/api/transcriptor';
import {
  templatesApi,
  type CreateDocumentTemplateRequest,
  type UpdateDocumentTemplateDraftRequest,
  type ListDocumentTemplatesParams,
  type RestoreDocumentTemplateRequest,
  type CloneDocumentTemplateRequest,
  type ListEncounterSchemasParams,
  type CreateEncounterSchemaRequest,
  type UpdateEncounterSchemaRequest,
  type RestoreEncounterSchemaRequest,
} from '@/shared/api/transcriptor/templatesApi';

import type {
  CatalogRead,
  DocumentTemplate,
  DocumentTemplateParseRequest,
  TemplateIssue,
} from '../config/types';

// A refused block template names each problem; the issues outlive the
// thunk's error serialization so the editor can mark where they are, as does
// a refusal because another version replaced the one the write was based on,
// or another save is still replacing it.
export type TemplateWriteError = SerializedError & {
  issues: TemplateIssue[];
  seriesMoved: boolean;
  seriesBusy: boolean;
};

const hasWriteFlag = (error: unknown, flag: 'seriesMoved' | 'seriesBusy') =>
  typeof error === 'object' &&
  error !== null &&
  (error as Record<string, unknown>)[flag] === true;

// A version write refused because it was based on a version another save
// replaced meanwhile, or is replacing.
export const isSeriesMovedWriteError = (error: unknown) =>
  hasWriteFlag(error, 'seriesMoved');

// The other save is still replacing that version: the series has not moved
// yet.
export const isSeriesBusyWriteError = (error: unknown) =>
  hasWriteFlag(error, 'seriesBusy');

// === Document Templates ===

export const fetchDocumentTemplates = createAsyncThunk(
  'templates/fetchDocumentTemplates',
  async (params: ListDocumentTemplatesParams) => {
    const response = await templatesApi.listDocumentTemplates({
      ...params,
      statuses: params.statuses ?? ['active', 'draft'],
    });

    return response;
  },
);

export const fetchDeletedDocumentTemplates = createAsyncThunk(
  'templates/fetchDeletedDocumentTemplates',
  async (params: Omit<ListDocumentTemplatesParams, 'statuses'>) => {
    const response = await templatesApi.listDocumentTemplates({
      ...params,
      statuses: ['deleted'],
    });

    return response;
  },
);

export const fetchDocumentTemplateById = createAsyncThunk(
  'templates/fetchDocumentTemplateById',
  async ({ id, organizationId }: CatalogRead) => {
    const template = await templatesApi.getDocumentTemplate(id, organizationId);

    return template;
  },
);

// The series' active version, the one a new version is checked against, also
// while a newer draft exists. It is not stored, so the template list keeps
// the version a page shows.
export const fetchActiveDocumentTemplate = createAsyncThunk(
  'templates/fetchActiveDocumentTemplate',
  async ({ id, organizationId }: CatalogRead) => {
    const template = await templatesApi.getDocumentTemplateBySeriesId(
      id,
      organizationId,
    );

    return template;
  },
);

export const createDocumentTemplateVersion = createAsyncThunk<
  DocumentTemplate,
  CreateDocumentTemplateRequest,
  {
    fulfilledMeta: { warnings: TemplateIssue[] };
    serializedErrorType: TemplateWriteError;
  }
>(
  'templates/createDocumentTemplateVersion',
  async (data, { fulfillWithValue }) => {
    const { template, warnings } =
      await templatesApi.createDocumentTemplate(data);

    return fulfillWithValue(template, { warnings });
  },
  {
    serializeError: (error) => ({
      ...miniSerializeError(error),
      issues: getTemplateIssues(error).map((issue) => toPlainMessage(issue)),
      seriesMoved: isTemplateSeriesMovedError(error),
      seriesBusy: isTemplateSeriesBusyError(error),
    }),
  },
);

export const updateDocumentTemplateDraft = createAsyncThunk(
  'templates/updateDocumentTemplateDraft',
  async ({
    id,
    data,
  }: {
    id: string;
    data: UpdateDocumentTemplateDraftRequest;
  }) => {
    const template = await templatesApi.updateDocumentTemplateDraft(id, data);

    return template;
  },
);

export const deleteDocumentTemplateThunk = createAsyncThunk(
  'templates/deleteDocumentTemplate',
  async (id: string) => {
    await templatesApi.deleteDocumentTemplate(id);

    return id;
  },
);

export const restoreDocumentTemplateThunk = createAsyncThunk(
  'templates/restoreDocumentTemplate',
  async ({
    id,
    data,
  }: {
    id: string;
    data?: RestoreDocumentTemplateRequest;
  }) => {
    await templatesApi.restoreDocumentTemplate(id, data);

    return id;
  },
);

export const cloneDocumentTemplateThunk = createAsyncThunk(
  'templates/cloneDocumentTemplate',
  async ({ id, data }: { id: string; data: CloneDocumentTemplateRequest }) => {
    const result = await templatesApi.cloneDocumentTemplate(id, data);

    return result;
  },
);

export const parseDocumentTemplateThunk = createAsyncThunk(
  'templates/parseDocumentTemplate',
  async (data: DocumentTemplateParseRequest, { signal }) => {
    const result = await templatesApi.parseDocumentTemplate(data, signal);

    return result;
  },
);

// === Encounter Schemas ===

export const fetchEncounterSchemas = createAsyncThunk(
  'templates/fetchEncounterSchemas',
  async (params: ListEncounterSchemasParams) => {
    const response = await templatesApi.listEncounterSchemas(params);

    return response;
  },
);

export const createEncounterSchemaThunk = createAsyncThunk(
  'templates/createEncounterSchema',
  async (data: CreateEncounterSchemaRequest) => {
    const schema = await templatesApi.createEncounterSchema(data);

    return schema;
  },
);

export const updateEncounterSchemaThunk = createAsyncThunk(
  'templates/updateEncounterSchema',
  async ({ id, data }: { id: string; data: UpdateEncounterSchemaRequest }) => {
    const schema = await templatesApi.updateEncounterSchema(id, data);

    return schema;
  },
);

export const fetchEncounterSchemaById = createAsyncThunk(
  'templates/fetchEncounterSchemaById',
  async ({ id, organizationId }: CatalogRead) => {
    const schema = await templatesApi.getEncounterSchema(id, organizationId);

    return schema;
  },
);

export const deleteEncounterSchemaThunk = createAsyncThunk(
  'templates/deleteEncounterSchema',
  async (id: string) => {
    await templatesApi.deleteEncounterSchema(id);

    return id;
  },
);

export const restoreEncounterSchemaThunk = createAsyncThunk(
  'templates/restoreEncounterSchema',
  async ({
    id,
    data,
  }: {
    id: string;
    data?: RestoreEncounterSchemaRequest;
  }) => {
    await templatesApi.restoreEncounterSchema(id, data);

    return id;
  },
);

export const cloneEncounterSchemaThunk = createAsyncThunk(
  'templates/cloneEncounterSchema',
  async ({
    id,
    data,
  }: {
    id: string;
    data: { target_organization_id: string };
  }) => {
    const result = await templatesApi.cloneEncounterSchema(id, data);

    return result;
  },
);
